/**
 * tx.micro — 微前端模块
 *
 * 两种模式：
 *   1. 主应用模式（HOST）：加载外部子应用到当前页面
 *      - registerApps() → start()：路由驱动的子应用注册
 *      - loadApp() → unmountApp()：手动挂载/卸载子应用到指定容器（如 SubApp 组件内）
 *   2. 子应用模式（SUB）：当前应用被其他主应用加载
 *      - isPoweredByHost()：检测是否运行在 qiankun 环境中
 *
 * 依赖：qiankun 需通过 CDN 外部脚本提前加载到 window.qiankun
 */
window.ttx = window.ttx || {};
ttx.micro = ttx.micro || {};

// ---------- 内部状态 ----------

/** 已注册的子应用配置列表 */
ttx.micro._apps = [];

/** 手动加载的子应用实例缓存，key 为子应用名称 */
ttx.micro._loadedApps = {};

/** qiankun start 是否已调用（避免重复启动） */
ttx.micro._started = false;

/** 注册回调：子应用加载/卸载时通知宿主（如显示 loading） */
ttx.micro._lifecycleCallbacks = {};

// ---------- 子应用模式（已有，保留） ----------

ttx.micro._container = null;
ttx.micro._poweredByHost = false;

/** 设置渲染容器（子应用模式） */
ttx.micro.setContainer = function (container) {
    ttx.micro._container = container || null;
};

/** 获取渲染容器 */
ttx.micro.getContainer = function () {
    return ttx.micro._container;
};

/** 标记当前应用是否被主应用加载 */
ttx.micro.setPoweredByHost = function (value) {
    ttx.micro._poweredByHost = value === true;
};

/** 检测是否运行在主应用（qiankun）环境中 */
ttx.micro.isPoweredByHost = function () {
    if (typeof window !== 'undefined' && typeof window.__POWERED_BY_QIANKUN__ !== 'undefined') {
        return window.__POWERED_BY_QIANKUN__ === true;
    }
    return ttx.micro._poweredByHost;
};

// ---------- 主应用模式（HOST）—— 新增 ----------

/**
 * 注册子应用列表
 * apps 格式：[{ name, entry, container, activeRule, props? }]
 * 调用后需再调用 start() 才会生效
 */
ttx.micro.registerApps = function (apps) {
    if (!Array.isArray(apps) || !apps.length) {
        ttx.log?.warn?.('[micro] registerApps: apps 为空');
        return;
    }
    ttx.micro._apps = apps;
    ttx.log?.success?.('[micro] 已注册 ' + apps.length + ' 个子应用');
};

/**
 * 启动 qiankun，使已注册的子应用按路由规则激活
 * opts: { sandbox?, prefetch?, ... } 透传给 qiankun.start
 */
ttx.micro.start = function (opts) {
    var qiankun = window.qiankun;
    if (!qiankun) {
        ttx.log?.error?.('[micro] qiankun 未加载，请确认 CDN 脚本已引入');
        return;
    }
    if (ttx.micro._started) {
        ttx.log?.warn?.('[micro] qiankun 已启动，跳过重复调用');
        return;
    }
    if (!ttx.micro._apps.length) {
        ttx.log?.warn?.('[micro] 无已注册子应用，先调用 registerApps 再 start');
        return;
    }

    // 注入生命周期回调，通知宿主应用状态变化
    var appsWithLifecycle = ttx.micro._apps.map(function (app) {
        var originalBootstrap = app.bootstrap;
        var originalMount = app.mount;
        var originalUnmount = app.unmount;
        return Object.assign({}, app, {
            bootstrap: function (props) {
                ttx.micro._notifyLifecycle(app.name, 'beforeBootstrap');
                return originalBootstrap ? originalBootstrap(props) : Promise.resolve();
            },
            mount: function (props) {
                ttx.micro._notifyLifecycle(app.name, 'beforeMount');
                var result = originalMount ? originalMount(props) : Promise.resolve();
                ttx.micro._notifyLifecycle(app.name, 'afterMount');
                return result;
            },
            unmount: function (props) {
                ttx.micro._notifyLifecycle(app.name, 'beforeUnmount');
                var result = originalUnmount ? originalUnmount(props) : Promise.resolve();
                ttx.micro._notifyLifecycle(app.name, 'afterUnmount');
                return result;
            }
        });
    });

    try {
        qiankun.registerMicroApps(appsWithLifecycle, {
            beforeLoad: [function (app) {
                ttx.micro._notifyLifecycle(app.name, 'beforeLoad');
                return Promise.resolve();
            }],
            afterMount: [],
            afterUnmount: []
        });
        // 使用 qiankun sandbox（开启样式隔离）
        qiankun.start(Object.assign({ sandbox: { experimentalStyleIsolation: true } }, opts || {}));
        ttx.micro._started = true;
        ttx.log?.success?.('[micro] qiankun 启动完成');
    } catch (e) {
        ttx.log?.error?.('[micro] qiankun 启动失败: ' + (e.message || e));
    }
};

/**
 * 手动加载一个子应用到指定 DOM 容器
 * 适用于容器内嵌子应用的场景（如 SubApp 组件），不走路由匹配
 * @param {Object} config  { name, entry, container }
 *   - name:      子应用名称（唯一标识）
 *   - entry:     子应用 HTML 入口地址
 *   - container: DOM 元素或选择器字符串
 *   - props:     传递给子应用的 props
 * @returns {Object|null} 子应用实例（含 mount/unmount/update 方法），失败返回 null
 */
ttx.micro.loadApp = function (config) {
    var qiankun = window.qiankun;
    if (!qiankun) {
        ttx.log?.error?.('[micro] qiankun 未加载');
        return null;
    }
    if (!config || !config.name || !config.entry || !config.container) {
        ttx.log?.error?.('[micro] loadApp 参数不完整: name/entry/container 均为必填');
        return null;
    }

    var sandboxConfig = typeof config.sandbox !== 'undefined'
        ? config.sandbox
        : (config.sandboxOptions || { experimentalStyleIsolation: true });

    // noCache: 强制所有资源（entry HTML + JS/CSS）绕过 HTTP 缓存重新请求（页签刷新场景）
    //
    // 【根因】qiankun loadMicroApp 内部用 `name-containerXpath` 作 key 缓存 parcel 生命周期（Ks Map，无清理机制）。
    // 同 name + 同 DOM 位置容器 → 缓存命中 → 直接复用旧 parcel，importEntry / 自定义 fetch 根本不执行。
    // 因此 noCache 场景必须更换实例名绕过该缓存，强制走真实加载，fetch 才会生效。
    var fetchFn = null;
    var loadName = config.name;
    if (config.noCache) {
        ttx.micro._noCacheSeq = (ttx.micro._noCacheSeq || 0) + 1;
        loadName = config.name + '-r' + ttx.micro._noCacheSeq;
        config._loadName = loadName; // 供 _doLoadMicroApp 使用（对外仍按原始 name 注册实例）
        ttx.log?.info?.('[micro] noCache 模式：实例名 ' + loadName + '，资源将绕过缓存重新请求');
        fetchFn = function (url, args) {
            return window.fetch(url, Object.assign({}, args, { cache: 'no-store' }));
        };
    }

    // 已加载则先卸载旧的，等待异步卸载完成后再加载，避免 single-spa error #32
    var prevApp = ttx.micro._loadedApps[config.name];
    if (prevApp) {
        delete ttx.micro._loadedApps[config.name];
        var unmountResult = null;
        try { unmountResult = prevApp.unmount(); } catch (e) {}

        if (unmountResult && unmountResult.then) {
            // 异步卸载：完成后加载新实例，返回带 mountPromise 的占位对象
            var placeholder = {
                mountPromise: new Promise(function (resolve) {
                    var doLoad = function () {
                        var app = ttx.micro._doLoadMicroApp(config, sandboxConfig, fetchFn);
                        if (app && app.mountPromise && app.mountPromise.then) {
                            app.mountPromise.then(resolve);
                        } else {
                            resolve();
                        }
                    };
                    unmountResult.then(doLoad).catch(doLoad);
                })
            };
            return placeholder;
        }
    }

    return ttx.micro._doLoadMicroApp(config, sandboxConfig, fetchFn);
};

/** 内部：实际调用 qiankun.loadMicroApp */
ttx.micro._doLoadMicroApp = function (config, sandboxConfig, fetchFn) {
    try {
        // noCache 场景下 name 已加实例后缀（绕过 qiankun 内部 parcel 缓存），
        // 对外仍按原始 name 注册，保证 unmountApp(name) / getApp(name) 兼容
        var loadName = config._loadName || config.name;
        var microAppConfig = {
            name: loadName,
            entry: config.entry,
            container: config.container,
            props: config.props || {}
        };
        var qiankunConfig = { sandbox: sandboxConfig };
        // qiankun configuration.fetch：import-html-entry 拉取 entry 及所有资源都走这个函数
        if (fetchFn) qiankunConfig.fetch = fetchFn;
        var app = window.qiankun.loadMicroApp(microAppConfig, qiankunConfig);
        ttx.micro._loadedApps[config.name] = app;
        ttx.log?.success?.('[micro] 子应用 ' + config.name + (loadName !== config.name ? '（实例 ' + loadName + '）' : '') + ' 加载成功');
        return app;
    } catch (e) {
        ttx.log?.error?.('[micro] 子应用 ' + config.name + ' 加载失败: ' + (e.message || e));
        return null;
    }
};

/**
 * 卸载手动加载的子应用
 * @param {string} name 子应用名称
 */
ttx.micro.unmountApp = function (name) {
    var app = ttx.micro._loadedApps[name];
    if (app) {
        try {
            app.unmount();
            ttx.log?.success?.('[micro] 子应用 ' + name + ' 已卸载');
        } catch (e) {
            ttx.log?.error?.('[micro] 卸载子应用 ' + name + ' 失败: ' + (e.message || e));
        }
        delete ttx.micro._loadedApps[name];
    }
};

/**
 * 获取已加载的子应用实例
 * @param {string} name 子应用名称
 * @returns {Object|null}
 */
ttx.micro.getApp = function (name) {
    return ttx.micro._loadedApps[name] || null;
};

/**
 * 注册生命周期回调：宿主可监听子应用加载/挂载/卸载事件
 * @param {string} event  'beforeLoad' | 'beforeMount' | 'afterMount' | 'beforeUnmount' | 'afterUnmount'
 * @param {Function} fn   (appName) => void
 */
ttx.micro.onLifecycle = function (event, fn) {
    if (!ttx.micro._lifecycleCallbacks[event]) {
        ttx.micro._lifecycleCallbacks[event] = [];
    }
    ttx.micro._lifecycleCallbacks[event].push(fn);
};

/** 内部：触发生命周期回调 */
ttx.micro._notifyLifecycle = function (appName, event) {
    var fns = ttx.micro._lifecycleCallbacks[event];
    if (fns && fns.length) {
        fns.forEach(function (fn) { try { fn(appName); } catch (e) {} });
    }
};
